import { useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { Bell, BellOff, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

// Profile-page card: enable / disable web push, plus configure how many
// hours before kickoff the user wants to be reminded. Pulled into its own
// component so it can be reused on a settings page later.

export function NotificationsCard() {
  const push = usePushSubscription();
  const [hoursBefore, setHoursBefore] = useState(2);
  const [savingHours, setSavingHours] = useState(false);

  useEffect(() => {
    api.push.prefs
      .get()
      .then((p) => {
        if (p?.hoursBefore != null) setHoursBefore(p.hoursBefore);
      })
      .catch(() => {});
  }, []);

  const saveHours = async (h: number) => {
    setHoursBefore(h);
    setSavingHours(true);
    try {
      await api.push.prefs.put({ hoursBefore: h, pushEnabled: true });
      toast.success(`Erinnerung ${h}h vor Anpfiff`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Speichern");
    } finally {
      setSavingHours(false);
    }
  };

  return (
    <section className="bg-card rounded-lg shadow-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        {push.state === "subscribed" ? <Bell className="h-5 w-5 text-success" /> : <BellOff className="h-5 w-5 text-muted-foreground" />}
        <h2 className="font-display font-bold text-base">Push-Benachrichtigungen</h2>
      </div>

      {push.state === "loading" && <div className="text-xs text-muted-foreground">Lade …</div>}

      {push.state === "unsupported" && (
        <div className="rounded-md bg-muted p-3 text-xs flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
          <div>
            Dein Browser unterstützt keine Web-Push-Benachrichtigungen. iOS funktioniert nur, wenn du das Tippspiel zum Home-Bildschirm hinzufügst.
          </div>
        </div>
      )}

      {push.state === "blocked" && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
          <div>
            Push-Berechtigung wurde geblockt. Aktiviere sie in den Browser-Einstellungen für diese Seite und lade die App neu.
          </div>
        </div>
      )}

      {push.state === "default" && (
        <>
          <p className="text-xs text-muted-foreground">
            Werde rechtzeitig erinnert, wenn deine Lieblingsspiele anstehen — kein Tipp mehr verpasst.
          </p>
          <Button onClick={push.subscribe} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
            <Bell className="h-4 w-4 mr-2" /> Push aktivieren
          </Button>
        </>
      )}

      {push.state === "subscribed" && (
        <>
          <div className="rounded-md bg-success/10 text-success text-xs px-3 py-2">
            ✅ Du bekommst Push-Benachrichtigungen vor jedem Spiel, das du noch nicht getippt hast.
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> Erinnerung … vor Anpfiff
            </label>
            <div className="flex gap-1">
              {[1, 2, 4, 12, 24].map((h) => (
                <button
                  key={h}
                  onClick={() => saveHours(h)}
                  disabled={savingHours}
                  className={`flex-1 text-xs font-bold py-2 rounded-md tap transition ${
                    hoursBefore === h ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {h}h
                </button>
              ))}
            </div>
          </div>

          <Button onClick={push.unsubscribe} variant="outline" className="w-full text-xs">
            <BellOff className="h-4 w-4 mr-2" /> Push deaktivieren
          </Button>
        </>
      )}

      {push.state === "error" && (
        <div className="rounded-md bg-destructive/10 text-destructive text-xs px-3 py-2 flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{push.error ?? "Fehler"}</span>
        </div>
      )}
    </section>
  );
}
